import { faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb'
import {
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Feedback from '../../../components/Feedback'
import RoundedPaper from '../../../components/RoundedPaper'
import COLORS from '../../../styles/colors'
import { TFunction } from 'next-i18next'

const MINIMUM_NUMBER_OF_VALIDATIONS = 3

type ValidationMessageValue = {
  checked: boolean
  value: string | ValidationMessageValue[] | ReactNode
}

const ValidationMessage = ({ checked, value }: ValidationMessageValue) => {
  return (
    <ListItem sx={{ alignItems: 'start' }}>
      <ListItemIcon>
        {checked ? (
          <CheckCircleOutlineIcon color="success" />
        ) : (
          <DoNotDisturbIcon color="warning" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={<Typography variant="inter_inputText">{value}</Typography>}
        sx={{ mt: 0 }}
      />
    </ListItem>
  )
}

export type CreatePasswordForm = {
  password: string
  confirmPassword: string
}

export type CreatePasswordFormErrors = {
  password: ValidationMessageValue[]
  confirmPassword: string
}

const validatePassword = (
  password: string,
  t: TFunction
): { errors: ValidationMessageValue[]; isValid: boolean } => {
  const errors: ValidationMessageValue[] = []

  const hasLowercase: RegExp = new RegExp('[a-z]+')
  const hasUppercase: RegExp = new RegExp('[A-Z]+')
  const hasNumbers: RegExp = new RegExp('[0-9]+')
  const hasSpecialCharacters: RegExp = new RegExp('[^A-Za-z0-9]+')

  const validation = [
    {
      predicate: hasLowercase,
      message: t('lowercaseLetters'),
    },
    {
      predicate: hasUppercase,
      message: t('uppercaseLetters'),
    },
    {
      predicate: hasNumbers,
      message: t('numbers'),
    },
    {
      predicate: hasSpecialCharacters,
      message: t('specialCharacters'),
    },
  ]

  errors.push({
    checked: password.length >= 8,
    value: t('atLeastCharacters'),
  })

  const validationMessage: ValidationMessageValue[] = []
  validation.forEach(({ predicate, message }) => {
    validationMessage.push({
      checked: predicate.test(password),
      value: message,
    })
  })

  errors.push({
    checked:
      validationMessage.filter(({ checked }) => {
        return checked
      }).length >= MINIMUM_NUMBER_OF_VALIDATIONS,
    value: validationMessage,
  })

  const isValid =
    validationMessage.filter(({ checked }) => !checked).length >= 3

  return { errors, isValid }
}

export type CreatePasswordFormProps = {
  error: boolean
  onSignInClick: () => void
  loading: boolean
  onSubmit: (password: string) => void
  tokenHasExpired: boolean
  userAlreadyExists: boolean
}

const CreatePasswordForm = ({
  error,
  loading,
  onSignInClick,
  onSubmit,
  tokenHasExpired,
  userAlreadyExists,
}: CreatePasswordFormProps) => {
  const [values, setValues] = useState<CreatePasswordForm>({
    password: '',
    confirmPassword: '',
  })
  const { t } = useTranslation('CreatePasswordForm')

  const handleInput = (key: 'password' | 'confirmPassword', value: string) => {
    setValues({
      ...values,
      [key]: value,
    })
  }

  const validatedPassword = validatePassword(values.password, t)

  const validationErrors: CreatePasswordFormErrors = {
    password: validatedPassword.errors,
    confirmPassword:
      values.confirmPassword !== values.password
        ? t('pleaseEnterSamePassword')
        : '',
  }

  const handleOnSubmit = async () => {
    if (formIsValid) {
      onSubmit(values.password)
    }
  }

  const handleKeyPress: React.KeyboardEventHandler<HTMLDivElement> = (
    event
  ) => {
    if (event.key === 'Enter') {
      handleOnSubmit()
    }
  }

  const canSubmit = () => {
    const confirmNewPasswordIsValid = values.password === values.confirmPassword
    const newPasswordIsValid =
      validationErrors.password.filter(({ checked }) => checked).length > 0

    return newPasswordIsValid && confirmNewPasswordIsValid
  }

  const formIsValid = canSubmit()

  return (
    <RoundedPaper sx={{ mb: '30px', width: '100%' }}>
      <Stack justifyItems="middle" gap="24px" width="100%">
        <Stack direction="row" gap="15px">
          <Typography
            color={COLORS.greyDark}
            textAlign="center"
            variant="garage_h4"
          >
            {tokenHasExpired ? t('linkExpired') : t('createPassword')}
          </Typography>
          {loading && <CircularProgress color="error" />}
        </Stack>

        {tokenHasExpired ? (
          <Typography>
            {t('verificationLinkExpired') + ' '}
            <a href={`mailto:${t('supportEmail')}`}>{t('supportEmail')}</a>.{' '}
          </Typography>
        ) : (
          <>
            <Stack gap="20px" width="100%">
              <Stack gap="16px" width="100%">
                <TextField
                  placeholder={t('password')}
                  variant="outlined"
                  type="password"
                  fullWidth
                  disabled={loading}
                  required
                  onChange={(event) =>
                    handleInput('password', event.target.value)
                  }
                  onKeyDown={handleKeyPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon
                          color="inherit"
                          icon={faLock}
                          style={{
                            height: '16px',
                            width: '16px',
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  placeholder={t('confirmPassword')}
                  variant="outlined"
                  helperText={validationErrors.confirmPassword}
                  onKeyDown={handleKeyPress}
                  type="password"
                  fullWidth
                  disabled={loading}
                  required
                  onChange={(event) =>
                    handleInput('confirmPassword', event.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon
                          color="inherit"
                          icon={faLock}
                          style={{
                            height: '16px',
                            width: '16px',
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!formIsValid || loading}
                  onClick={handleOnSubmit}
                >
                  {t('completeRegistration')}
                </Button>
              </Stack>
              {error && (
                <Feedback type="error" text={t('createPasswordError')} />
              )}
              {userAlreadyExists && (
                <Feedback
                  type="error"
                  additionalContent={
                    <Typography>
                      {t('userAlreadyExistsError') + ' '}
                      <Typography
                        onClick={onSignInClick}
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {t('clicingHere')}
                      </Typography>
                      <Typography>
                        {t('needHelp') + ' '}
                        <a
                          href={`mailto:${t('supportEmail')}`}
                          style={{ color: COLORS.burnedOrange }}
                        >
                          {t('supportEmail')}
                        </a>
                      </Typography>
                    </Typography>
                  }
                />
              )}
              <Divider />
              <Stack gap="10px" py="15px">
                <Typography fontWeight="bold" variant="inter_inputText">
                  {t('passwordMustContain')}
                </Typography>
                <List>
                  {validationErrors.password.map(
                    ({ checked, value }, index) => {
                      const result = Array.isArray(value) ? (
                        <React.Fragment key={`validation_sub_${index}`}>
                          <Typography>
                            {t('atLeastOfTheFollowing', {
                              count: MINIMUM_NUMBER_OF_VALIDATIONS,
                            })}
                          </Typography>
                          <List>
                            {value.map((item, index) => {
                              return (
                                <ValidationMessage
                                  checked={item.checked}
                                  key={`validation_sub2_${index}`}
                                  value={item.value}
                                />
                              )
                            })}
                          </List>
                        </React.Fragment>
                      ) : (
                        value
                      )

                      return (
                        <ValidationMessage
                          checked={checked}
                          key={`validation_${index}`}
                          value={result}
                        />
                      )
                    }
                  )}
                </List>
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </RoundedPaper>
  )
}

export default CreatePasswordForm
